import Card, { CardBody, CardHeader, CardRow } from "components/partials/card/card";
import SkeletonLoader from "containers/admin/clients/touchpoint/tab-components/details-tab/components/skeleton-loader";
import { EmailWriterDetailsSectionSkeleton } from "containers/admin/clients/touchpoint/tab-components/details-tab/components/email-writer-details-section-skeleton";

import type { EmailAttributes } from "hooks/api/use-touchpoint-versions";
import { useCurrentCampaign } from "hooks/api/use-campaigns";

import { useEmailContentSettings } from "containers/admin/clients/touchpoint/tab-components/details-tab/hooks/use-email-content-settings";
import { getVersionStatusTexts } from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab.helper";
import {
  TouchpointCardProps,
  EmailContentSettingsData,
} from "containers/admin/clients/touchpoint/tab-components/details-tab/details-tab.types";
import { useIsEmailWriterGenerating } from "containers/admin/clients/touchpoint/hooks/use-is-email-writer-generating";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

/** Chakra `lineHeights.5`. A bare `5` here is a unitless CSS ratio — 5x the card's font size. */
const ROW_LINE_HEIGHT = "1.25rem";

const renderEmailContentSettingsCardBody = (
  isLoading: boolean,
  emailData: EmailAttributes | null,
  emailContentSettingsData: EmailContentSettingsData,
) => {
  if (isLoading) {
    return <SkeletonLoader />;
  } else if (!emailData) {
    return <div className="text-sm text-gray-500 py-2">No email data found</div>;
  }
  return Object.values(emailContentSettingsData).map((data) => (
    <CardRow
      key={data.title}
      title={data.title}
      value={data.value}
      sx={{ lineHeight: ROW_LINE_HEIGHT }}
    />
  ));
};

const EmailVersionContentSettingsCard = ({
  touchpoint,
  selectedVersion,
  ...rest
}: TouchpointCardProps) => {
  const currentCampaign = useCurrentCampaign();
  const isTemplate = currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
  const isEmailWriterGenerating = useIsEmailWriterGenerating();
  const { emailData, isLoading } = useEmailContentSettings(touchpoint.id, selectedVersion);

  if (!selectedVersion) {
    return null;
  }

  if (isEmailWriterGenerating) {
    return (
      <Card {...rest}>
        <CardHeader heading="Version content settings" />
        <CardBody>
          <EmailWriterDetailsSectionSkeleton />
        </CardBody>
      </Card>
    );
  }

  const emailContentSettingsData = {
    emailType: {
      title: "Email Type",
      value: emailData?.iterableEmailTypeName,
    },
    senderName: {
      title: "Sender name",
      value: emailData?.senderName || "Not defined",
    },
    fromEmail: {
      title: "From email",
      value: emailData?.senderEmail || "Not defined",
    },
    replyToEmail: {
      title: "Reply-to email",
      value: emailData?.replyToEmail || "Not defined",
    },
    ...(!isTemplate && {
      subjectLine: {
        title: "Subject line",
        value: emailData?.subjectLine,
      },
      preheader: {
        title: "Preheader",
        value: emailData?.preheader,
      },
    }),
  };

  return (
    <Card {...rest}>
      <CardHeader heading="Version content settings" {...getVersionStatusTexts(selectedVersion)} />
      <CardBody>
        {renderEmailContentSettingsCardBody(isLoading, emailData, emailContentSettingsData)}
      </CardBody>
    </Card>
  );
};

export default EmailVersionContentSettingsCard;
